/* Support.jsx — public /support page (web only).
 *
 * An optional one-off payment to help build JAI faster. It is NOT a purchase:
 * it unlocks nothing and must never change anyone's plan. The server creates a
 * Stripe Checkout session in payment mode (not a subscription) tagged
 * type=support — see backend/src/services/supportPaymentService.js.
 *
 * Works signed in or signed out (a login token, if present, only lets the
 * server attach the payment to the supporter's Stripe customer).
 *
 * WEB ONLY: App.jsx does not register this route on the native app, the same
 * way /pricing is left out (Apple Guideline 3.1.1). A native build never
 * reaches this page, and nothing inside the app links to it.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { JaiMark, JaiWordmark, JAI_COLORS, JAI_FONTS } from '../components/JaiBrand'
import { ArrowLeft, ErrorBanner } from '../components/AuthFields'
import { supporterApi } from '../services/api'
import { useBackToApp } from '../utils/appReturn'

// Used only if GET /supporter/config can't be reached. The server re-checks
// every amount, so a stale number here can never get a payment through.
const FALLBACK = { presets: [10, 15, 20], min: 5, max: 500 }

export default function Support() {
  const navigate = useNavigate()
  const back = useBackToApp()

  const [cfg, setCfg] = useState(FALLBACK)
  const [selected, setSelected] = useState(null) // a preset, or 'custom'
  const [custom, setCustom] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    supporterApi.getConfig()
      .then(({ data }) => { if (!cancelled && data?.presets) setCfg(data) })
      .catch(() => { /* keep the fallback */ })
    return () => { cancelled = true }
  }, [])

  const customNumber = custom.trim() === '' ? NaN : Number(custom)
  const amount = selected === 'custom' ? customNumber : selected

  const validate = () => {
    if (!amount) return 'Choose an amount first.'
    if (!Number.isInteger(amount)) return 'Enter a whole-dollar amount.'
    if (amount < cfg.min) return `The minimum is $${cfg.min}.`
    if (amount > cfg.max) return `The maximum is $${cfg.max}.`
    return ''
  }

  const go = async () => {
    const problem = validate()
    if (problem) { setError(problem); return }
    setError('')
    setBusy(true)
    try {
      const { data } = await supporterApi.startCheckout(amount)
      window.location.href = data.url // hand off to Stripe
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start checkout. Please try again.')
      setBusy(false)
    }
  }

  const pill = (active) => ({
    flex: '1 1 0', minWidth: 76, padding: '14px 10px', borderRadius: 12, cursor: 'pointer',
    fontFamily: JAI_FONTS.tight, fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em',
    border: `1.5px solid ${active ? JAI_COLORS.blue : 'rgba(10,31,68,0.14)'}`,
    background: JAI_COLORS.white,
    color: active ? JAI_COLORS.blue : JAI_COLORS.navy,
  })

  return (
    <div style={{ minHeight: 'calc(100vh / var(--jai-zoom))', background: JAI_COLORS.paper, fontFamily: JAI_FONTS.tight, display: 'flex', flexDirection: 'column' }}>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '16px 26px', borderBottom: '1px solid rgba(10,31,68,0.07)', background: 'rgba(246,248,251,0.85)', backdropFilter: 'blur(10px)', position: 'sticky', top: 0, zIndex: 20 }}>
        <button type="button" onClick={() => navigate(back.to)} title={back.label}
          style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <JaiMark size={30} />
          <JaiWordmark size={19} />
        </button>
        <button type="button" onClick={() => navigate(back.to)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, border: '1px solid rgba(10,31,68,0.18)', borderRadius: 999, padding: '7px 14px 7px 11px', cursor: 'pointer', color: JAI_COLORS.mute, fontSize: 12.5, fontWeight: 600, fontFamily: JAI_FONTS.tight }}>
          <ArrowLeft size={14} /> {back.label}
        </button>
      </header>

      <main style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '48px 20px 64px' }}>
        <div style={{ width: '100%', maxWidth: 480 }}>
          <h1 style={{ color: JAI_COLORS.navy, fontSize: 30, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 12px' }}>
            Support JAI
          </h1>
          <p style={{ color: JAI_COLORS.mute, fontSize: 15, lineHeight: 1.6, margin: '0 0 28px' }}>
            Support JAI is a completely optional, one-off payment that goes straight into building JAI
            faster. It doesn't unlock any features and isn't tax-deductible.
          </p>

          {error && <div style={{ marginBottom: 14 }}><ErrorBanner>{error}</ErrorBanner></div>}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {cfg.presets.map((n) => (
              <button key={n} type="button" style={pill(selected === n)}
                onClick={() => { setSelected(n); setError('') }}>
                ${n}
              </button>
            ))}
            <button type="button" style={{ ...pill(selected === 'custom'), fontSize: 15 }}
              onClick={() => { setSelected('custom'); setError('') }}>
              Other
            </button>
          </div>

          {selected === 'custom' && (
            <div style={{ marginTop: 14 }}>
              <label htmlFor="support-amount" style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: JAI_COLORS.mute, marginBottom: 6 }}>
                Amount in US dollars (${cfg.min} to ${cfg.max})
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: JAI_COLORS.white, border: '1.5px solid rgba(10,31,68,0.14)', borderRadius: 12, padding: '0 14px' }}>
                <span style={{ color: JAI_COLORS.navy, fontWeight: 700, fontSize: 18 }}>$</span>
                <input
                  id="support-amount" type="number" inputMode="numeric" min={cfg.min} max={cfg.max} step="1"
                  value={custom} onChange={(e) => { setCustom(e.target.value); setError('') }}
                  onKeyDown={(e) => { if (e.key === 'Enter') go() }}
                  placeholder="25" autoFocus
                  style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', padding: '14px 0', fontSize: 18, fontWeight: 700, color: JAI_COLORS.navy, fontFamily: JAI_FONTS.tight, minWidth: 0 }}
                />
              </div>
            </div>
          )}

          <button type="button" onClick={go} disabled={busy || !amount}
            style={{
              width: '100%', marginTop: 22, padding: '14px 16px', borderRadius: 12, border: 'none',
              cursor: busy || !amount ? 'not-allowed' : 'pointer', color: 'white', fontSize: 15, fontWeight: 700,
              fontFamily: JAI_FONTS.tight,
              background: busy || !amount ? 'rgba(30,91,214,0.45)' : `linear-gradient(135deg, ${JAI_COLORS.blueBright} 0%, ${JAI_COLORS.blue} 55%, #1A4FBF 100%)`,
            }}>
            {busy ? 'Opening checkout…' : amount && Number.isFinite(amount) ? `Support JAI with $${amount}` : 'Choose an amount'}
          </button>

          <p style={{ color: JAI_COLORS.mute, fontSize: 12.5, lineHeight: 1.6, marginTop: 16 }}>
            Payment is handled by Stripe and charged in US dollars. We email you a receipt.
            Nothing here changes your plan.
          </p>
        </div>
      </main>
    </div>
  )
}
